import { ColorPickerModule } from 'ngx-color-picker';
import { CommonModule, PercentPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxErrorsModule } from '@ultimate/ngxerrors';
import { HighchartsChartModule } from 'highcharts-angular';
import { OwlDateTimeModule } from 'ng-pick-datetime';
import { ModalModule } from 'ngx-boot-modal';
import { NgxPaginationModule } from 'ngx-pagination';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { ChartComponent } from './components/charts/chart/chart.component';
import { SynchronizedChartsComponent } from './components/charts/synchronized-charts/synchronized-charts.component';
import { AutocompleteFieldComponent } from './components/forms/autocomplete-field/autocomplete-field.component';
import { MultiselectFieldComponent } from './components/forms/multiselect-field/multiselect-field.component';
import { CustomCheckboxFieldComponent } from './components/forms/custom-checkbox-field/custom-checkbox-field.component';
import { CheckboxFieldComponent } from './components/forms/checkbox-field/checkbox-field.component';
import { CheckboxValueFieldComponent } from './components/forms/checkbox-value-field/checkbox-value-field.component';
import { DatepickerFieldComponent } from './components/forms/datepicker-field/datepicker-field.component';
import { ColorpickerFieldComponent } from './components/forms/colorpicker-field/colorpicker-field.component';
import { FieldErrorsComponent } from './components/forms/field-errors/field-errors.component';
import { FileFieldComponent } from './components/forms/file-field/file-field.component';
import { FileInputComponent } from './components/forms/file-input/file-input.component';
import { CustomFileFieldComponent } from './components/forms/custom-file-field/custom-file-field.component';
import { CustomFileInputComponent } from './components/forms/custom-file-input/custom-file-input.component';
import { InputEmailFieldComponent } from './components/forms/input-email-field/input-email-field.component';
import { InputFieldComponent } from './components/forms/input-field/input-field.component';
import { InputIntegerFieldComponent } from './components/forms/input-integer-field/input-integer-field.component';
import { InputNumberFieldComponent } from './components/forms/input-number-field/input-number-field.component';
import { InputPasswordFieldComponent } from './components/forms/input-password-field/input-password-field.component';
import { InputRangeFieldComponent } from './components/forms/input-range-field/input-range-field.component';
import { InputTextFieldComponent } from './components/forms/input-text-field/input-text-field.component';
import { InputTimeFieldComponent } from './components/forms/input-time-field/input-time-field.component';
import { RadioFieldComponent } from './components/forms/radio-field/radio-field.component';
import { SelectFieldComponent } from './components/forms/select-field/select-field.component';
import { TextareaFieldComponent } from './components/forms/textarea-field/textarea-field.component';
import { CollapsiblePanelComponent } from './components/collapsible-panel/collapsible-panel.component';
import { ConfirmModalComponent } from './components/modals/confirm-modal/confirm-modal.component';
import { EditionModalComponent } from './components/modals/edition-modal/edition-modal.component';
import { InfoModalComponent } from './components/modals/info-modal/info-modal.component';
import { SortControlComponent } from './components/sort-control/sort-control.component';
import { FormCellComponent } from './components/tables/form-table/form-cell/form-cell.component';
import { FormColumnComponent } from './components/tables/form-table/form-column/form-column.component';
import { FormTableColumnComponent } from './components/tables/form-table/form-table-column/form-table-column.component';
import { FormTableComponent } from './components/tables/form-table/form-table.component';
import { ColumnComponent } from './components/tables/sortable-table/column/column.component';
import { SortableCellComponent } from './components/tables/sortable-table/sortable-cell/sortable-cell.component';
import { SortableColumnComponent } from './components/tables/sortable-table/sortable-column/sortable-column.component';
import { SortableTableComponent } from './components/tables/sortable-table/sortable-table.component';
import { TransferListComponent } from './components/transfer-list/transfer-list.component';
import { IntegerInputDirective } from './directives/integer-input.directive';
import { TitleDirective } from './directives/title.directive';
import { ToggleFullscreenDirective } from './directives/toggle-full-screen.directive';
import { CapitalizeAllPipe } from './pipes/capitalize-all.pipe';
import { CapitalizePipe } from './pipes/capitalize.pipe';
import { DayDiffPipe } from './pipes/day-diff.pipe';
import { IfNullPipe } from './pipes/if-null.pipe';
import { IntLoopPipe } from './pipes/int-loop.pipe';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';
import { MeanPercentagePipe } from './pipes/mean-percentage.pipe';
import { PercentagePipe } from './pipes/percentage.pipe';
import { SafePipe } from './pipes/safe.pipe';
import { SortPipe } from './pipes/sort.pipe';
import { TimePipe } from './pipes/time.pipe';
import { DurationPipe } from './pipes/duration.pipe';
import { DpDatePickerModule } from 'ng2-date-picker';
import { DragScrollModule } from 'ngx-drag-scroll';
// tslint:disable-next-line:max-line-length
import { BaseManufacturingOrderFilterComponent } from './components/general/base-manufacturing-order-filter/base-manufacturing-order-filter.component';
import { EditMaterialFormComponent } from './components/modals/edit-material-form/edit-material-form.component';
import { DeliveryDateFormComponent } from './components/modals/delivery-date-form/delivery-date-form.component';
import { DeliveryDateValidator } from './components/modals/delivery-date-form/delivery-date-validator';
import { MaterialFormComponent } from './components/modals/material-form/material-form.component';
import { ManufacturingOrderChangeFormComponent } from './components/modals/manufacturing-order-comment-form/manufacturing-order-comment-form.component';
import { ManufacturingOrderObservationsFormComponent } from './components/modals/manufacturing-order-observations-form/manufacturing-order-observations-form.component';
import { ManufacturingOrderPiecesChangeFormComponent } from './components/modals/manufacturing-order-pieces-change-form/manufacturing-order-pieces-change-form.component';
import { PieceQuantityValidation } from './components/modals/manufacturing-order-pieces-change-form/piece-quantity-validator';
import { UrgentOrClaimedFormComponent } from './components/modals/urgent-or-claimed-form/urgent-or-claimed-form.component';
// tslint:disable-next-line:max-line-length
import { SchedulerTaskProgramFormComponent } from './components/modals/scheduler-task-program-form/scheduler-task-program-form.component';

@NgModule({
  declarations: [
    // Components
    AutocompleteFieldComponent,
    ChartComponent,
    CheckboxFieldComponent,
    CustomCheckboxFieldComponent,
    CheckboxValueFieldComponent,
    CollapsiblePanelComponent,
    ColumnComponent,
    ConfirmModalComponent,
    DatepickerFieldComponent,
    ColorpickerFieldComponent,
    EditionModalComponent,
    FieldErrorsComponent,
    FileFieldComponent,
    FileInputComponent,
    CustomFileFieldComponent,
    CustomFileInputComponent,
    FormCellComponent,
    FormColumnComponent,
    FormTableColumnComponent,
    FormTableComponent,
    InfoModalComponent,
    InputEmailFieldComponent,
    InputFieldComponent,
    InputIntegerFieldComponent,
    InputNumberFieldComponent,
    InputPasswordFieldComponent,
    InputTextFieldComponent,
    InputTimeFieldComponent,
    InputRangeFieldComponent,
    RadioFieldComponent,
    MultiselectFieldComponent,
    SelectFieldComponent,
    SortableColumnComponent,
    SortableCellComponent,
    SortableTableComponent,
    SortControlComponent,
    SynchronizedChartsComponent,
    TextareaFieldComponent,
    TransferListComponent,
    BaseManufacturingOrderFilterComponent,
    EditMaterialFormComponent,
    DeliveryDateFormComponent,
    MaterialFormComponent,
    ManufacturingOrderChangeFormComponent,
    ManufacturingOrderObservationsFormComponent,
    ManufacturingOrderPiecesChangeFormComponent,
    UrgentOrClaimedFormComponent,
    SchedulerTaskProgramFormComponent,
    // Pipes
    CapitalizePipe,
    CapitalizeAllPipe,
    DayDiffPipe,
    IfNullPipe,
    IntLoopPipe,
    LocalizedDatePipe,
    MeanPercentagePipe,
    PercentagePipe,
    SafePipe,
    SortPipe,
    TimePipe,
    DurationPipe,
    // Directives
    IntegerInputDirective,
    TitleDirective,
    ToggleFullscreenDirective
  ],
  imports: [
    CommonModule,
    HighchartsChartModule,
    ModalModule,
    NgSelectModule,
    NgxErrorsModule,
    NgxPaginationModule,
    OwlDateTimeModule,
    DpDatePickerModule,
    ColorPickerModule,
    DragScrollModule,
    PerfectScrollbarModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule
  ],
  exports: [
    // Modules
    CommonModule,
    ModalModule,
    NgxErrorsModule,
    NgxPaginationModule,
    OwlDateTimeModule,
    DpDatePickerModule,
    ColorPickerModule,
    DragScrollModule,
    PerfectScrollbarModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    // Components
    AutocompleteFieldComponent,
    ChartComponent,
    CheckboxFieldComponent,
    CustomCheckboxFieldComponent,
    CheckboxValueFieldComponent,
    ColumnComponent,
    DatepickerFieldComponent,
    ColorpickerFieldComponent,
    EditionModalComponent,
    FieldErrorsComponent,
    FileFieldComponent,
    FileInputComponent,
    CustomFileFieldComponent,
    CustomFileInputComponent,
    FormTableColumnComponent,
    FormTableComponent,
    InfoModalComponent,
    InputEmailFieldComponent,
    InputIntegerFieldComponent,
    InputNumberFieldComponent,
    InputPasswordFieldComponent,
    InputTextFieldComponent,
    InputTimeFieldComponent,
    InputRangeFieldComponent,
    RadioFieldComponent,
    MultiselectFieldComponent,
    SelectFieldComponent,
    SortableTableComponent,
    SortControlComponent,
    SynchronizedChartsComponent,
    TextareaFieldComponent,
    TransferListComponent,
    CollapsiblePanelComponent,
    ConfirmModalComponent,
    BaseManufacturingOrderFilterComponent,
    EditMaterialFormComponent,
    DeliveryDateFormComponent,
    MaterialFormComponent,
    ManufacturingOrderChangeFormComponent,
    ManufacturingOrderObservationsFormComponent,
    ManufacturingOrderPiecesChangeFormComponent,
    UrgentOrClaimedFormComponent,
    SchedulerTaskProgramFormComponent,
    // Pipes
    CapitalizeAllPipe,
    CapitalizePipe,
    DayDiffPipe,
    IfNullPipe,
    IntLoopPipe,
    LocalizedDatePipe,
    MeanPercentagePipe,
    PercentagePipe,
    SafePipe,
    SortPipe,
    TimePipe,
    DurationPipe,
    // Directives
    IntegerInputDirective,
    TitleDirective,
    ToggleFullscreenDirective
  ],
  providers: [
    // Pipes
    DayDiffPipe,
    LocalizedDatePipe,
    PercentagePipe,
    PercentPipe,
    TimePipe,
    DurationPipe,
    // Validators
    PieceQuantityValidation,
    DeliveryDateValidator
  ]
})
export class SharedModule { }



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/shared/shared.module.ts